/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { ClosedEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import {
  ListAuditLogsResult,
  ListAuditLogsResult$inboundSchema,
} from "../components/listauditlogsresult.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";

export type ListAuditLogsSecurity = {
  apikeyHeaderGramKey?: string | undefined;
  sessionHeaderGramSession?: string | undefined;
};

/**
 * Category of entries to list: 'configuration' (the default) for changes to the platform's configuration, or 'security' for machine access and other security-relevant activity such as workloads admitting themselves.
 */
export const QueryParamCategory = {
  Configuration: "configuration",
  Security: "security",
} as const;
/**
 * Category of entries to list: 'configuration' (the default) for changes to the platform's configuration, or 'security' for machine access and other security-relevant activity such as workloads admitting themselves.
 */
export type QueryParamCategory = ClosedEnum<typeof QueryParamCategory>;

export type ListAuditLogsRequest = {
  /**
   * The cursor for paginating through audit logs.
   */
  cursor?: string | undefined;
  /**
   * Project slug to filter audit logs to a specific project.
   */
  projectSlug?: string | undefined;
  /**
   * Actor ID to filter audit logs to a specific actor.
   */
  actorId?: string | undefined;
  /**
   * Action to filter audit logs to a specific action.
   */
  action?: string | undefined;
  /**
   * Subject type to filter audit logs to a specific kind of subject. When omitted, assistant activity events are excluded; pass 'assistant' to list them.
   */
  subjectType?: string | undefined;
  /**
   * Subject ID to filter audit logs to a specific subject (e.g. a single assistant).
   */
  subjectId?: string | undefined;
  /**
   * Subject IDs to filter audit logs to a set of subjects at once, e.g. a resource together with the child resources whose events name the child as the subject. Matches any listed subject of any subject type, except that assistant activity events stay excluded unless subject_type is 'assistant'; combine with subject_type to pin the kind. Blank entries are ignored.
   */
  subjectIds?: Array<string> | undefined;
  /**
   * Acting surface to filter audit logs to changes made through one surface, e.g. 'platform_mcp' to review agent-driven activity alone.
   */
  actingSurface?: string | undefined;
  /**
   * Inclusive start of the window to list changes from. Omit for the whole history.
   */
  from?: Date | undefined;
  /**
   * Exclusive end of the window to list changes from. Omit for the whole history.
   */
  to?: Date | undefined;
  /**
   * Category of entries to list: 'configuration' (the default) for changes to the platform's configuration, or 'security' for machine access and other security-relevant activity such as workloads admitting themselves.
   */
  category?: QueryParamCategory | undefined;
  /**
   * API Key header
   */
  gramKey?: string | undefined;
  /**
   * Session header
   */
  gramSession?: string | undefined;
};

export type ListAuditLogsResponse = {
  result: ListAuditLogsResult;
};

/** @internal */
export type ListAuditLogsSecurity$Outbound = {
  "apikey_header_Gram-Key"?: string | undefined;
  "session_header_Gram-Session"?: string | undefined;
};

/** @internal */
export const ListAuditLogsSecurity$outboundSchema: z.ZodMiniType<
  ListAuditLogsSecurity$Outbound,
  ListAuditLogsSecurity
> = z.pipe(
  z.object({
    apikeyHeaderGramKey: z.optional(z.string()),
    sessionHeaderGramSession: z.optional(z.string()),
  }),
  z.transform((v) => {
    return remap$(v, {
      apikeyHeaderGramKey: "apikey_header_Gram-Key",
      sessionHeaderGramSession: "session_header_Gram-Session",
    });
  }),
);

export function listAuditLogsSecurityToJSON(
  listAuditLogsSecurity: ListAuditLogsSecurity,
): string {
  return JSON.stringify(
    ListAuditLogsSecurity$outboundSchema.parse(listAuditLogsSecurity),
  );
}

/** @internal */
export const QueryParamCategory$outboundSchema: z.ZodMiniEnum<
  typeof QueryParamCategory
> = z.enum(QueryParamCategory);

/** @internal */
export type ListAuditLogsRequest$Outbound = {
  cursor?: string | undefined;
  project_slug?: string | undefined;
  actor_id?: string | undefined;
  action?: string | undefined;
  subject_type?: string | undefined;
  subject_id?: string | undefined;
  subject_ids?: Array<string> | undefined;
  acting_surface?: string | undefined;
  from?: string | undefined;
  to?: string | undefined;
  category: string;
  "Gram-Key"?: string | undefined;
  "Gram-Session"?: string | undefined;
};

/** @internal */
export const ListAuditLogsRequest$outboundSchema: z.ZodMiniType<
  ListAuditLogsRequest$Outbound,
  ListAuditLogsRequest
> = z.pipe(
  z.object({
    cursor: z.optional(z.string()),
    projectSlug: z.optional(z.string()),
    actorId: z.optional(z.string()),
    action: z.optional(z.string()),
    subjectType: z.optional(z.string()),
    subjectId: z.optional(z.string()),
    subjectIds: z.optional(z.array(z.string())),
    actingSurface: z.optional(z.string()),
    from: z.optional(z.pipe(z.date(), z.transform(v => v.toISOString()))),
    to: z.optional(z.pipe(z.date(), z.transform(v => v.toISOString()))),
    category: z._default(QueryParamCategory$outboundSchema, "configuration"),
    gramKey: z.optional(z.string()),
    gramSession: z.optional(z.string()),
  }),
  z.transform((v) => {
    return remap$(v, {
      projectSlug: "project_slug",
      actorId: "actor_id",
      subjectType: "subject_type",
      subjectId: "subject_id",
      subjectIds: "subject_ids",
      actingSurface: "acting_surface",
      gramKey: "Gram-Key",
      gramSession: "Gram-Session",
    });
  }),
);

export function listAuditLogsRequestToJSON(
  listAuditLogsRequest: ListAuditLogsRequest,
): string {
  return JSON.stringify(
    ListAuditLogsRequest$outboundSchema.parse(listAuditLogsRequest),
  );
}

/** @internal */
export const ListAuditLogsResponse$inboundSchema: z.ZodMiniType<
  ListAuditLogsResponse,
  unknown
> = z.pipe(
  z.object({
    Result: ListAuditLogsResult$inboundSchema,
  }),
  z.transform((v) => {
    return remap$(v, {
      "Result": "result",
    });
  }),
);

export function listAuditLogsResponseFromJSON(
  jsonString: string,
): SafeParseResult<ListAuditLogsResponse, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => ListAuditLogsResponse$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'ListAuditLogsResponse' from JSON`,
  );
}
